/** Small pure helpers for dates, names and message text. */

export function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((w) => w[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;

export function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: d.getFullYear() === today.getFullYear() ? undefined : "numeric" });
}

export function sameDay(a: string, b: string): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

export type Segment = { kind: "text"; text: string } | { kind: "link"; text: string } | { kind: "mention"; text: string; userId: string };

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Splits a message into text, links and mentions of known people, for safe
 * rendering as React nodes (never as HTML).
 */
export function segments(body: string, people: { id: string; name: string }[]): Segment[] {
  const names = [...people]
    .flatMap((p) => {
      const first = firstName(p.name);
      const sameFirst = people.filter((q) => firstName(q.name).toLowerCase() === first.toLowerCase()).length === 1;
      return sameFirst && first !== p.name ? [{ id: p.id, label: p.name }, { id: p.id, label: first }] : [{ id: p.id, label: p.name }];
    })
    .sort((a, b) => b.label.length - a.label.length);
  const mentionPart = names.length ? `|@(?:${names.map((n) => escapeRe(n.label)).join("|")})(?![\\p{L}\\p{N}_])` : "";
  const re = new RegExp(`(https?:\\/\\/[^\\s<]+[^\\s<.,;:!?)"'\\]]${mentionPart})`, "giu");
  const out: Segment[] = [];
  let last = 0;
  for (const m of body.matchAll(re)) {
    const i = m.index ?? 0;
    if (i > last) out.push({ kind: "text", text: body.slice(last, i) });
    const t = m[0];
    if (t.startsWith("@")) {
      const hit = names.find((n) => n.label.toLowerCase() === t.slice(1).toLowerCase());
      out.push(hit ? { kind: "mention", text: t, userId: hit.id } : { kind: "text", text: t });
    } else out.push({ kind: "link", text: t });
    last = i + t.length;
  }
  if (last < body.length) out.push({ kind: "text", text: body.slice(last) });
  return out;
}
